import React, { useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react'
import { translate } from '../Services/translation'
import { tcEventClickAddToCartEvent, tcEventClickRemoveFromCartEvent, tcEventProductListView } from "../Actions/TagCommander.action"
import MultiCarousel from './MultiCarousel'
import ProductCardSmall from './ProductCardSmall'
import { Checkmark } from './Icons/Checkmark'

const scrollIndicatorTimeBeforeShow = 1500 // Used to give some time to the user before to scroll indicator shows
const scrollIndicatorTimeBeforeHide = 3500  // Used to give some time to the user to see the scroll indicator before it disappears
const scrollIndicatorScrollMargin = 100 // Used to give some room so that we do not show scroll indicator if there is only a little bit left to scroll

let firstRender = true

const MiniCart = (props) => {
    const { quantity, orderRows, orderTotal, totalDiscount, productTotalInclVat, checkoutUrl, showInfo, itemHasBeenAdded, toggle, update, discount, orderTotalWithoutDiscount, deliveryCost, accessories, accessoriesTagCommanderProductList } = props;
    const [quantityState, setQuantityState] = useState(quantity)
    const [quantityUpdated, setQuantityUpdated] = useState(false)
    const [isOpen, setIsOpen] = useState(false) 
    const [isClosed, setIsClosed] = useState(undefined)
    const [isScrollable, setIsScrollable] = useState(false)
    const [accessoriesCount, setAccessoriesCount] = useState(0)
    const cartRef = useRef(null)
    const cartContentRef = useRef(null)

    useLayoutEffect(() => {
        /* Handles cart toggle from both inside the component and outside the component through dispatch(toggle) propagation from showInfo property */
        if (showInfo == undefined) return

        if(showInfo) {
            setIsOpen(true)
            setIsClosed(false)
        }
        else {
            setIsOpen(false)
            setIsClosed(true)
        }
    }), [showInfo]

    useLayoutEffect(() => {
        let vh = window.innerHeight * 0.01;
        cartRef.current.style.setProperty('--cart-vh-unit', `${vh}px`);

        window.addEventListener('resize', () => {
            // We dynamically change the --cart-vh-unit custom prop to calculate the correct height for the cart, 
            // since mobile browsers behave very differently, and often change their viewport on scroll, which
            // can cause the cart to be too small or too large.
            vh = window.innerHeight * 0.01;
            cartRef.current.style.setProperty('--cart-vh-unit', `${vh}px`);
        });

        firstRender = false
    }, [])

    useEffect(() => {
        if (quantity > quantityState) {
            setQuantityUpdated(true)
            setTimeout(() => setQuantityUpdated(false), 1000)
        }

        setQuantityState(quantity)
    }, [quantity])

    // Call tcEventProductListView with the accessory list
    useEffect(() => {
        const { productList } = accessoriesTagCommanderProductList

        if (productList == null || productList.length == 0) return
        if (accessoriesCount == productList.length) return // Do not call tcEventProductListView if the number of products is the same (e.g. when we update the quantity

        setAccessoriesCount(productList.length)
        tcEventProductListView(productList)
    }, [accessoriesTagCommanderProductList])

    useLayoutEffect(() => {
        const cartContent = cartContentRef.current

        if (isOpen == false) return
        if (cartContent == null) return

        const currentScrollPosition = Math.floor(cartContent.scrollTop + cartContent.clientHeight) + scrollIndicatorScrollMargin

        if (cartContent.scrollHeight <= currentScrollPosition) return

        if (cartContent.scrollHeight > cartContent.clientHeight) {
            setTimeout(() => {
                setIsScrollable(true)

                setTimeout(() => {
                    setIsScrollable(false)
                }, scrollIndicatorTimeBeforeHide)
            }, scrollIndicatorTimeBeforeShow)
        }
    }, [isOpen, orderRows])
    
    const handleUpdateClick = (order, currentQuantity, originalQuantity) => {
		if (currentQuantity == originalQuantity) {
			return;
		}

		if (currentQuantity < originalQuantity) {
            update(order.articleNumber, order.quantity-=1)
		}
		else {
            update(order.articleNumber, order.quantity+=1)
		}
	}

	const handleDeleteClick = (order) => {
		update(order.articleNumber, 0)
		tcEventClickRemoveFromCartEvent(order)
	}

    const carouselSettings = {
        responsive: {
            mobile: {
                breakpoint: {max: 1024, min: 0},
                items: 1,
                paritialVisibilityGutter: 75,
            },
            desktop: {
              breakpoint: {max: 9999, min: 1024},
              items: 2,
              paritialVisibilityGutter: 25,
            },
        }   
    }

    const MemoMultiCarousel = useMemo(() => MultiCarousel, [accessoriesTagCommanderProductList.productList.length])

    return (
        <>
            <button className="cart__button" title={ translate('minicart.checkoutLabel') } onClick={() => toggle()}>
                <i className="cart__icon" >
                    <span className={`cart__quantity ${quantityUpdated ? 'cart__quantity--updated' : ''}`}>{ quantity }</span>
                </i>
                <span className='cart__label'>
                    { translate('minicart.checkoutLabel') }
                </span>
            </button>

            <div className={`cart__backdrop 
                ${isOpen ? 'cart__backdrop--open' : ''}
                ${isClosed ? 'cart__backdrop--closed' : ''}`}
                onClick={() => isOpen && toggle()}>
            </div>

            <div ref={cartRef} className={`cart__info 
                ${isOpen ? 'cart__info--open' : ''}
                ${isClosed ? 'cart__info--closed' : ''}`}>

                <h3 className='cart__header'>

                    {
                        itemHasBeenAdded
                            ? <span className='cart__heading cart__heading--item-added'>
                                <Checkmark />
                                {translate('checkout.cart.itemHasBeenAdded')}
                            </span>
                            : <span className='cart__heading'>{translate('checkout.cart.title')}</span>
                    }

                    <button className="button-alt button-alt--black cart__close-button" title={translate('minicart.checkout.close')} onClick={() => toggle()}>
                        <span>{ translate('minicart.checkout.close') }</span>
                        <span className="material-icons">
                            close
                        </span>
                    </button>
                </h3>

                <div className="cart__content" ref={cartContentRef}>         
                    { 
                        orderRows.length == 0 && <p className='cart__empty'>{ translate('minicart.empty') }</p>
                    }  
                    { 
                        orderRows.map((order, key) => (
                        <div className="cart__product" key={key}>
                            <div className="cart__image">
                                <img src={order.image} alt={order.name} loading='lazy' />
                            </div>
                            <div className="cart__product-info">
                                <a href={order.url} className="cart__product-name">
                                    {/* <div>{order.website}</div> */}
                                    <div>{order.name}</div>
                                </a>
                                <div className="cart__product-details-container">
                                    <div className="cart__product-details">
                                        <div className="cart__product-details-input">
                                            <button className="cart__product-remove-btn" onClick={() => handleDeleteClick(order)}></button>
                                            <div className="cart__quantity-input">
                                                <button onClick={() => handleUpdateClick(order, order.quantity-1, order.quantity)}>
                                                    <span className="material-icons">
                                                        remove
                                                    </span>
                                                </button>
                                                <input type="text" disabled value={ order.quantity } />
                                                <button onClick={() => handleUpdateClick(order, order.quantity+1, order.quantity)}>
                                                    <span className="material-icons">
                                                        add 
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                        <div className="cart__prices">
                                            {
                                                order.campaignPrice && 
                                                    <>
                                                        <div className="cart-price cart-price--campaign">{ order.campaignPrice }</div>
                                                        <div className="cart-price cart-price--original">{ order.price }</div>
                                                    </>
                                            }
                                            {
                                                !order.campaignPrice && 
                                                    <div className="cart-price">{ order.price }</div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        ))
                    }

                    {
                        accessories.length > 0 && (
                            <div className='cart__accessories'>
                                <MemoMultiCarousel {...{ carouselSettings, accessories, heading: translate('minicart.accessories.heading') }}>
                                    {
                                        accessories.map((product, key) => (
                                            <ProductCardSmall key={key} {...{ product, theme: "" }} />
                                        ))
                                    }
                                </MemoMultiCarousel>
                            </div>
                        )
                    }
      
                    <ScrollIndicator {...{ isScrollable }} />            
                </div>

                <div className="cart__info-summary">
                    <div className="cart__info-row">
                        <p>{ translate('minicart.heading.total') }</p>
                        <p>{ productTotalInclVat }</p>
                    </div>
                    <div className="cart__info-row">
                        <p>{ translate('checkout.order.discount') }</p>
                        <p className="cart__discount">{ totalDiscount }</p>
                    </div>
                    <div className="cart__info-row cart__info-total">
                        <p>{ translate('minicart.total') }</p>
                        <p className=""> { orderTotal }</p>
                    </div>
                    <a href={ checkoutUrl } className="cart__checkout-button button button--primary">{ translate('minicart.checkout') }</a>
                </div>
            </div>
        </>
    )
}

const ScrollIndicator = ({ isScrollable }) => {

    return (
        <div className={`cart__scroll-indicator ${isScrollable ? 'cart__scroll-indicator--show' : 'cart__scroll-indicator--hide'}`}>
            <span>
                { translate('minicart.scrollmsg') }
            </span>
        </div>
    )
}


export default MiniCart;